import { Alert, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Card } from "@avenue-8/sidekick-ui";
import type { CrmSyncState } from "@features/Contacts/hooks/useCrmSyncState";
import type { PendingSyncStateDto } from "@shared/generated/crm/models/PendingSyncStateDto";
import styled from "@emotion/styled";

type ActiveSyncState = Exclude<
  CrmSyncState,
  { status: "loading" } | { status: "fetch-error" } | { status: "disabled" }
>;

const KNOWN_TYPES: Record<string, { plural: string }> = {
  email: { plural: "Emails" },
  memo: { plural: "Memos" },
};

function mergeCounts(a: string, b: string): string {
  const hasPlus = a.endsWith("+") || b.endsWith("+");
  const aVal = parseInt(a.replace(/[^0-9]/g, ""));
  const bVal = parseInt(b.replace(/[^0-9]/g, ""));
  if(hasPlus) {
    return Math.max(aVal, bVal) + "+";
  }
  return String(aVal + bVal);
}

const itemIsDone = (entry: PendingSyncStateDto): boolean => {
  return entry.toFetch === "0" && entry.toProcess === "0";
}

function entryStatus(entry: PendingSyncStateDto) {
  const isSyncing = entry.toFetch !== "0" || entry.toProcess !== "0";
  if (isSyncing && entry.lastFailure) return { label: "Syncing with errors", color: "error" } as const;
  if (isSyncing) return { label: "Syncing", color: "default" } as const;
  if (entry.lastFailure) return { label: "Error", color: "error" } as const;
  return { label: "Up to date", color: "success" } as const;
}

type Props = {
  open: boolean;
  onClose: () => void;
  syncState: ActiveSyncState;
};

const ResourceCardHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export const CrmSyncDetailsDialog = ({ open, onClose, syncState }: Props) => {
  const { status, entries, hasErrors } = syncState;
  const statePhrase =
    status === "syncing"
      ? "Your CRM data is currently being synced."
      : hasErrors
        ? "Some CRM data could not be synced."
        : "Your CRM data is up to date.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="crm-sync-details-title">
      <DialogTitle id="crm-sync-details-title">
        <span>
          CRM Sync Status
        </span>
        <IconButton aria-label="Close dialog" size="small" onClick={onClose} sx={{ position: "absolute", right: 16, top: 16 }}>
          <CloseIcon fontSize="small"/>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">{statePhrase}</Typography>
        </Box>
        {status === "syncing" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Depending on the number of items, the data sync process can take anywhere from a few minutes to a few hours.
            <br />
            Status may take up to 10 minutes to reflect the latest changes.
          </Alert>
        )}
        {entries.map((e) => {
          const typeLabel = KNOWN_TYPES[e.type]?.plural ?? e.type;
          const entryChip = entryStatus(e);
          const done = itemIsDone(e);
          return (
            <Card key={`${e.type}-${e.provider}`} sx={{ mb: 1, padding: 2 }}>
              <ResourceCardHeader>
                <Typography variant="subtitle2">{typeLabel} ({e.provider})</Typography>
                <Chip label={entryChip.label} color={entryChip.color} size="small" />
              </ResourceCardHeader>
              {!done && (
                <Typography variant="body1">{mergeCounts(e.toFetch, e.toProcess)} items in progress</Typography>
              )}
              {done && !e.lastFailure && <Typography variant="body1">All items synced</Typography>}
              {done && e.lastFailure && <Typography variant="body1">Some items failed to sync</Typography>}
              <Typography variant="body2" color="text.secondary">
                Updated: {new Date(e.updatedAt).toLocaleString()}
              </Typography>
              {e.lastFailure && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body1" color="error">
                    {e.lastFailure.reason || "Unknown error"}
                  </Typography>
                  <Typography variant="body2" color="error">
                    {new Date(e.lastFailure.at).toLocaleString()}
                  </Typography>                  
                </Box>
              )}
            </Card>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};
